import React, { useCallback } from 'react';
import ListCourses from '../../components/ListCourses';
import { useDashboardFilters, useDashboardActions } from './hooks';
import styled from 'styled-components';
import { Filter, Page, Toggler, Button, DashboardCourseCard } from '../../ui';
import { CONTENT_STANDARD, media } from '../../utils/media';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../ui/Page/PageHeader';
import PageControls from '../../ui/Page/PageControls';
import { useUserRole, useHasRequiredLevel } from '../auth/hooks';
import { useFeatures } from '../settings/hooks';
import { useNavigate } from 'react-router-dom';
import NetworkSuggestions from '../../components/CoursioNetwork';
import PageSection from '../../ui/Page/PageSection';

const FilterGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 10px;

  ${media.lessThan('phablet')} {
    flex-direction: column;
    gap: 10px;
    flex-wrap: wrap;
  }
`;

const DashboardContentOld = ({ openModal, handleSearch, searchQuery, clearSearch, setSortMode, dashboardData, isLoading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { filterOptions: filters, studentFilterOptions: filtersStudents, currentFilter: selectedFilter } = useDashboardFilters();
  const { setDashboardFilter } = useDashboardActions();

  const role = useUserRole();
  const isStudent = role === 'reader';
  const isOwner = useHasRequiredLevel('owner');

  const myCourses = dashboardData || [];
  const activeFilters = isStudent ? filtersStudents : filters;
  const filteredMyCourses = (myCourses || []).filter(activeFilters[selectedFilter]?.fnc || activeFilters[0].fnc);

  const features = useFeatures();

  const goToCourse = useCallback(
    (item) => {
      navigate(`/view/course/${item.id}`);
    },
    [navigate],
  );

  const goToEdit = useCallback(
    (item) => {
      if (features?.modernCourseBuilder) {
        // Modern route: ID-based
        navigate(`/edit/course/${item.id}`);
      } else {
        // Legacy route: slug-based
        navigate(`/course/${item.ownerSlug}/${item.slug}`, { state: item });
      }
    },
    [navigate, features],
  );

  const goToSettings = useCallback(
    (item) => {
      navigate(`/courses/${item.id}/settings`);
    },
    [navigate],
  );

  const handleSortMode = useCallback(
    (checked) => {
      setSortMode(checked);
    },
    [setSortMode],
  );

  const handleOptions = useCallback(
    (value) => {
      setDashboardFilter(+value);
    },
    [setDashboardFilter],
  );

  return (
    <Page
      gap='60'
      staticElements={
        <PageHeader>
          <PageControls
            onClearSearch={clearSearch}
            onSearch={handleSearch}
            placeholder={t('dashboard.searchPlaceholder')}
            disableClear={!searchQuery}
            openModelButton={
              isOwner && (
                <Button icon='Edit' onClick={openModal}>
                  {t('course.createCourse')}
                </Button>
              )
            }
          >
            <FilterGroup>
              <Filter info={isStudent ? filtersStudents : filters} handleFilter={handleOptions} selected={selectedFilter} />
              {!isStudent && <Toggler leftText={t('dashboard.lastVisited')} rightText={t('dashboard.lastCreated')} onClick={handleSortMode} />}
            </FilterGroup>
          </PageControls>
        </PageHeader>
      }
    >
      <PageSection $size={CONTENT_STANDARD}>
        <ListCourses
          showProgress={true}
          showButtons={false}
          module={'dashboard'}
          items={filteredMyCourses}
          CardComponent={DashboardCourseCard}
          isLoading={isLoading && !myCourses.length}
          isUpdating={isLoading && !!myCourses.length}
          goToCoursePage={goToCourse}
          goToEditCourse={goToEdit}
          goToSettings={goToSettings}
        />
      </PageSection>
      {/* Coursio Network Recommendations */}
      <PageSection $size={CONTENT_STANDARD}>{features.coursioNetwork && <NetworkSuggestions />}</PageSection>
    </Page>
  );
};

export default DashboardContentOld;
